import { faLocationDot, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faClock, faUser } from '@fortawesome/free-regular-svg-icons';
import { getAutoComplete, getSearchData } from 'services/transfer';
import { SearchData, SelectGrouped } from 'ts/transfer/interfaces/Transfer';
import { SearchTransferModel } from 'ts/transfer/classes/SearchTransferModel';
import FormGroupDatepicker from 'components/ui/FormGroupDatepicker/FormGroupDatepicker';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import FormGroupTimePicker from 'components/ui/FormGroupTimePicker/FormGroupTimePicker';
import RadioGroup from 'components/ui/RadioGroup/RadioGroup';
import { Select } from 'ts/interfaces/Generic';
import Button from 'components/ui/Button/Button';
import { searchModes } from 'constants/transfer';
import { addDays } from 'utils/utils';
import { onClickClose, onClickOverlay } from 'utils/responsive';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getSearchParams } from 'utils/transfer';
import * as idb from 'utils/shared/idbUrl';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { MainContext } from 'contexts/MainContext';
import { b2b } from 'constants/generic';

const SearchTransfer: FC = () => {
  const [t] = useTranslation();
  const [searchData, setSearchData] = useState<SearchData>({ people: [] });
  const [model, setModel] = useState(new SearchTransferModel());
  const [locationsFrom, setLocationsFrom] = useState<SelectGrouped[]>([]);
  const [locationsTo, setLocationsTo] = useState<SelectGrouped[]>([]);
  const navigate = useNavigate();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [urlParams] = useSearchParams();
  const location = useLocation();
  const previousController = useRef(new AbortController());
  const { userSiteConfig } = useContext(MainContext);
  const isResultPage = location.pathname.includes('results');
  const [reload, setReload] = useState(false);

  useEffect(() => {
    const b2bPath = ['/transfers', '/transfers/results', '/'];
    if (b2b && !b2bPath.includes(location.pathname)) {
      return;
    }

    (async () => {
      if (urlParams.get('countryCodeFrom')) {
        const params = new SearchTransferModel(urlParams);
        setModel(params);
        const options: Select[] = [
          {
            label: params.pickup.desc,
            value: params.pickup.code,
            data: {
              lat: +params.pickup.latitude,
              lng: +params.pickup.longitude,
              type: params.pickup.type,
            },
          },
          {
            label: params.dropoff.desc,
            value: params.dropoff.code,
            data: {
              lat: +params.dropoff.latitude,
              lng: +params.dropoff.longitude,
              type: params.dropoff.type,
            },
          },
        ];
        const airports = {
          label: t('transfer.airports'),
          options: options.filter((item) => item.data.type === 0),
        };
        const addresses = {
          label: t('transfer.addresses'),
          options: options.filter((item) => item.data.type === 1),
        };
        setLocationsFrom([airports, addresses]);
        setLocationsTo([airports, addresses]);
      }

      const data = await getSearchData();
      setSearchData(data);
    })();

    if (!isResultPage) {
      const newModel = new SearchTransferModel();
      setIsSubmitted(false);
      setModel(newModel);
      setReload(true);
      setTimeout(() => {
        setReload(false);
      }, 100);
    }
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  const onClickSearch = async () => {
    setIsSubmitted(true);
    idb.deletePageStateByPartialKey('transfers');
    if (model.pickup.code && model.dropoff.code) {
      const params = getSearchParams(model);

      const element = document.querySelector('.search-wrapper') as HTMLElement;
      element?.classList.remove('visible-responsive');

      navigate(`/transfers/results?${params}`);
    }
  };

  const onChangeDate = (date: Date, fieldName: 'departureDate' | 'returnDate') => {
    const newDate = new Date(date);
    newDate.setHours(model[fieldName].getHours(), model[fieldName].getMinutes(), 0, 0);
    if (fieldName === 'departureDate' && addDays(1, newDate) > model.returnDate) {
      setModel({ ...model, [fieldName]: newDate, returnDate: addDays(7, newDate) });
    } else {
      setModel({ ...model, [fieldName]: newDate });
    }
  };

  const onLocationInputChange = async (term: string, isFrom: boolean) => {
    if (term.length > 1) {
      if (previousController.current) {
        previousController.current.abort();
      }

      const conroller = new AbortController();
      previousController.current = conroller;
      const newLocations = await getAutoComplete(term, conroller);
      if (isFrom) {
        setLocationsFrom(newLocations);
      } else {
        setLocationsTo(newLocations);
      }
    }
  };

  const onLocationChange = (code: string, field: string, locs: SelectGrouped[]) => {
    const airportOrPoint = locs.find((item) => item.options.some((val) => val.value === code))!;
    const location = airportOrPoint.options.find((item) => item.value === code)!;
    if (location.data?.lat && location.data?.lng) {
      const newModel = {
        ...model,
        [field]: {
          code: location.value + '',
          desc: location.label,
          latitude: location.data.lat,
          longitude: location.data.lng,
          type: 0,
        },
      };
      if (field === 'pickup') {
        newModel.countryCodeFrom = location.data.countryCode;
      } else {
        newModel.countryCodeTo = location.data.countryCode;
      }
      setModel(newModel);
    } else {
      var geocoder = new google.maps.Geocoder();
      geocoder.geocode({ placeId: location.value as string }, function (results) {
        if (results?.length) {
          const lat = results[0].geometry.location.lat();
          const lon = results[0].geometry.location.lng();
          const newModel = {
            ...model,
            [field]: {
              code: location.value,
              desc: location.label,
              latitude: lat + '',
              longitude: lon + '',
              type: 1,
            },
          };

          const country = results[0].address_components.find((item) =>
            item.types.includes('country')
          );
          const code = country?.short_name || '';
          if (field === 'pickup') {
            newModel.countryCodeFrom = code;
          } else {
            newModel.countryCodeTo = code;
          }

          setModel(newModel);
        }
      });
    }
  };

  const onClickNewSearch = () => {
    navigate('/transfers');
    idb.deletePageStateByPartialKey('transfers');
    setTimeout(() => {
      const latestPageUrl = localStorage.getItem('latestPageUrl');
      if (latestPageUrl) {
        const latestPageUrlObj = JSON.parse(latestPageUrl);
        localStorage.setItem(
          'latestPageUrl',
          JSON.stringify({ ...latestPageUrlObj, transfer: [] })
        );
      }
    }, 300);
  };

  if (reload) {
    return null;
  }

  return (
    <div className="search-wrapper" onClick={onClickOverlay}>
      <div className="search-inner">
        <div className="results-responsive-header">
          <span className="title text-md">{t('search.search')}</span>
          <FontAwesomeIcon icon={faTimes} onClick={onClickClose} />
        </div>
        {isResultPage && (b2b || userSiteConfig.base.rememberModeEnabled) && (
          <div className="search-element radius">
            <Button
              title={t('home.new_search')}
              onClick={onClickNewSearch}
              className="btn btn-default"
            />
          </div>
        )}
        <div className="search-element mode-transfer">
          <RadioGroup
            options={searchModes.slice(0, 2).reverse()}
            value={model.searchType}
            onChange={(searchType) => setModel({ ...model, searchType })}
            needToTranslate={true}
          />
        </div>
        <div className="search-element location-transfer mb-10 select-custom__transfer">
          <SelectCustom
            value={model.pickup.code}
            onInputChange={(val) => onLocationInputChange(val, true)}
            onChange={(val) => onLocationChange(val, 'pickup', locationsFrom)}
            options={locationsFrom}
            movablePlaceholder={t('transfer.from_search')}
            movablePlaceholderIcon={faLocationDot}
            invalid={isSubmitted && !model.pickup.code}
            filterOption={() => true}
            menuPlacement={'bottom'}
          />
        </div>
        <div className="search-element">
          <FormGroupDatepicker
            label={t('flight.departure_date')}
            value={model.departureDate}
            onChange={(value) => onChangeDate(value, 'departureDate')}
            movablePlaceholder={t('flight.departure_date')}
            movablePlaceholderIcon={faCalendar}
            longFormat={true}
            minDate={addDays(1)}
            onClickReset={() => setModel({ ...model, departureDate: addDays(7) })}
          />
        </div>
        <div className="search-element time">
          <FormGroupTimePicker
            value={model.departureDate}
            movablePlaceholder={t('transfer.time')}
            onChange={(departureDate) => setModel({ ...model, departureDate })}
            movablePlaceholderIcon={faClock}
          />
        </div>
        <div className="search-element nationality pr-0">
          <SelectCustom
            options={searchData.people}
            value={model.people}
            onChange={(people) => setModel({ ...model, people })}
            movablePlaceholder={t('passenger.s')}
            movablePlaceholderIcon={faUser}
            menuPlacement={'bottom'}
          />
        </div>
        {isResultPage && (b2b || userSiteConfig.base.rememberModeEnabled) && (
          <div className="search-element radius"></div>
        )}
        <div className="search-element mode-transfer"></div>

        <div className="search-element location-transfer select-custom__transfer">
          <SelectCustom
            value={model.dropoff.code}
            onInputChange={(val) => onLocationInputChange(val, false)}
            onChange={(val) => onLocationChange(val, 'dropoff', locationsTo)}
            options={locationsTo}
            movablePlaceholder={t('transfer.to_search')}
            movablePlaceholderIcon={faLocationDot}
            invalid={isSubmitted && !model.dropoff.code}
            filterOption={() => true}
            menuPlacement={'bottom'}
          />
          {model.countryCodeFrom &&
            model.countryCodeTo &&
            model.countryCodeFrom !== model.countryCodeTo && (
              <div className="color-danger text-sm mt-10">{t('transfer.diff_countries')}</div>
            )}
        </div>
        {model.searchType === '1' && (
          <Fragment>
            <div className="search-element">
              <FormGroupDatepicker
                label={t('flight.return_date')}
                value={model.returnDate}
                onChange={(value) => onChangeDate(value, 'returnDate')}
                movablePlaceholder={t('flight.return_date')}
                movablePlaceholderIcon={faCalendar}
                longFormat={true}
                minDate={addDays(1)}
                onClickReset={() =>
                  setModel({
                    ...model,
                    returnDate:
                      model.departureDate > addDays(14)
                        ? addDays(1, model.departureDate)
                        : addDays(14),
                  })
                }
              />
            </div>
            <div className="search-element time">
              <FormGroupTimePicker
                value={model.returnDate}
                movablePlaceholder={t('transfer.time')}
                onChange={(returnDate) => setModel({ ...model, returnDate })}
                movablePlaceholderIcon={faClock}
              />
            </div>
          </Fragment>
        )}

        <div className="search-button ml-a">
          <button className="btn btn-primary btn-go" onClick={onClickSearch}>
            GO
          </button>
        </div>
      </div>
    </div>
  );
};

export default withErrorBoundary(SearchTransfer, 'SearchTransfer');
